/**
 * AUTO TYPE NAMING (casework/naming round, 2026-08-09 — docs/MODELER.md): per-category
 * hardcoded recipes derive a type's display NAME from its own params — "as much human
 * involvement in these conventions as we can hard code is good" (Matt). The name is
 * DERIVED-BUT-MATERIALIZED: `commands/library.ts` calls `deriveTypeName` on every
 * type-param write and stores the result as the type's plain `name` field, so every
 * other reader (search, schedules, the firm library) keeps seeing an ordinary string —
 * nothing else in the app needs to know a name is computed.
 *
 * Covers the categories whose params fully determine a sensible name: assembly-backed
 * (wall/floor/roof/ceiling — makeup from core+finish materials, thickness ALWAYS shown
 * — it's cheap and never wrong to include, which trivially satisfies "distinguishing"
 * without needing a sibling-collision check), door/window (operation, FORM-ONLY per the
 * OPENING CONFIGURATION GOVERNANCE round, 2026-08-10 — size and muntins moved to
 * instance and are deliberately excluded from the name; frame/glazing/park-mode/
 * sidelight/transom appended only when they distinguish a real construction
 * difference), and casework (style "kind" + depth family — WIDTH is deliberately
 * excluded per the type/instance governance round: width is an instance param now,
 * "many many widths for the same type"). Every other category keeps its manually-typed
 * name (column/beam/stair/ramp/railing/object — additive later, per Matt).
 *
 * THE FIRM-LIBRARY TRAP (recorded 2026-08-09, must stay true): this function must run
 * IDENTICALLY for a model's own types (`commands/library.ts#computeTypeName`) and for
 * firm-library records curated in TemplatesWorkspace (`BimFirmLibraryEditor.jsx`) —
 * both import it from here, neither reimplements it. `firmLibrary.ts#findType` already
 * keys adoption on `firmId` first, falling back to a byName match only for
 * un-subscribed models — that fallback stays correct only as long as both sides
 * compute names the same way. If this recipe ever changes, old un-touched in-doc
 * records keep their PREVIOUSLY MATERIALIZED name until something re-triggers a write
 * (`createType`/`updateType`/`setTypeParam`, or the `materializeTypeNames` backfill run
 * at every doc-open) — they do not retroactively rename themselves the instant this
 * file changes. Re-run `firmLibrary.test.ts`'s adopt-by-name coverage after touching
 * this file.
 */
import type { AssemblyRecord, CategoryId, MaterialId, MaterialRecord, ParamValue } from './types'
import { assemblyThickness } from './params'
import { wallCoreLabel } from './wallCore'
import { floorCoreLabel } from './floorCore'
import { roofCoreLabel } from './roofCore'
import { ceilingCoreLabel } from './ceilingCore'
// formatFtInExplicit, NOT formatFtIn: auto type names are MATERIALIZED identity
// strings (stored on models AND in the firm library, matched byte-for-byte by
// adopt-by-name) — the 2026-08-17 zero-feet display suppression must never
// silently reshape them (`Stem Wall 0'-8"` stays exactly that).
import { formatFtInExplicit as formatFtIn } from './units'
import { DOOR_OPERATIONS, WINDOW_OPERATIONS } from './categories/openingOps'
import { fixtureKindLabel, isCeilingFixtureKind } from './categories/fixture/fixtureKinds'

export const NAME_RECIPE_CATEGORIES: ReadonlySet<CategoryId> = new Set<CategoryId>([
  'wall', 'floor', 'roof', 'ceiling', 'door', 'window', 'casework',
  // FOOTINGS round: the new type-only families are recipe-covered from birth —
  // section dims (+ material when set) fully determine a sensible name
  'footing', 'stem',
  // ELECTRICAL FIXTURES round: the kind alone determines the name (`Duplex
  // Outlet`, `Switch`) — mount height is instance-only, never named
  'fixture',
])

function materialName(materials: MaterialRecord[], id: MaterialId | string | undefined | null): string {
  if (!id) return ''
  return materials.find((m) => m.id === id)?.name ?? ''
}

/** Core + finish material makeup — deterministic regardless of how materials are named.
 *  `coreLabel` (WALL CORE DEFINITION round, 2026-08-18): walls pass the label derived
 *  from their structured core params ('2×6 Wood Stud', 'CMU') in place of the raw
 *  core-layer material names — size lives on the type's core definition now, not in
 *  the material's name ('Wood Stud' alone would name nothing distinguishing). */
function assemblyMakeup(
  assembly: AssemblyRecord | null,
  materials: MaterialRecord[],
  coreLabel?: string,
): string {
  if (!assembly || !assembly.layers.length) return 'Unassigned'
  const { start, end } = assembly.coreBoundary
  const coreNames = coreLabel ? [coreLabel] : [...new Set(
    assembly.layers.slice(start, end + 1).map((l) => materialName(materials, l.materialId)).filter(Boolean),
  )]
  const finishNames = [...new Set(
    assembly.layers
      .filter((l, i) => l.function === 'finish' && (i < start || i > end))
      .map((l) => materialName(materials, l.materialId))
      .filter(Boolean),
  )]
  const parts = [coreNames.join(' + '), ...finishNames].filter(Boolean)
  return parts.length ? parts.join(' · ') : 'Unassigned'
}

function assemblyBackedName(
  category: CategoryId,
  params: Record<string, ParamValue>,
  assembly: AssemblyRecord | null,
  materials: MaterialRecord[],
): string {
  // params.coreType is present only for a DECLARED core (computeTypeName strips the
  // resolved default when the raw params never declared one) — undeclared records
  // keep naming from their actual core-layer material. A concrete floor's
  // floorCoreLabel and a finish-only ceiling's ceilingCoreLabel are null on purpose
  // (Slab vs Paving / the finish materials name those).
  const declared = params.coreType !== undefined
  const coreLabel = !declared ? undefined
    : category === 'wall' ? wallCoreLabel(params)
    : category === 'floor' ? floorCoreLabel(params) ?? undefined
    : category === 'roof' ? roofCoreLabel(params)
    : category === 'ceiling' ? ceilingCoreLabel(params) ?? undefined
    : undefined
  // FURRING (Matt, 2026-10-19): a stud wall bare on one side — nothing outside its
  // core on that face (it's built against another wall) — names itself a furring wall
  const isFurring = category === 'wall' && !!coreLabel && !!assembly && assembly.layers.length > 0
    && (params.coreType === 'wood' || params.coreType === 'metal')
    && ((assembly.coreBoundary.start === 0) !== (assembly.coreBoundary.end === assembly.layers.length - 1))
  const makeup = assemblyMakeup(assembly, materials, isFurring ? `${coreLabel} Furring` : coreLabel)
  const thickness = formatFtIn(assemblyThickness(assembly))
  const fn = category === 'wall' ? `${params.function === 'interior' ? 'Int' : 'Ext'} · ` : ''
  return `${fn}${makeup} — ${thickness}`
}

const DOOR_OP_LABEL = new Map<string, string>(DOOR_OPERATIONS.map((o) => [o.value, o.label]))
const WINDOW_OP_LABEL = new Map<string, string>(WINDOW_OPERATIONS.map((o) => [o.value, o.label]))
/** SITE GATES: the non-default infill styles (picket-over-panel reads unadorned) */
const GATE_STYLE_LABEL: Record<string, string> = { picket: 'Full Pickets', solid: 'Solid', slat: 'Slats', vPlank: 'V-Groove Planks' }

/**
 * FORM-ONLY (OPENING CONFIGURATION GOVERNANCE round, 2026-08-10): size and muntins are
 * instance params now and are correctly absent from `params` here (this is called with
 * TYPE-schema-resolved values only) — no defaulting/formatting for them belongs in this
 * function anymore. `parkMode`/`sidelight`/`transom` are added to `extras` because,
 * pre-round, distinct SIZE alone accidentally disambiguated seed types that share every
 * other type param (e.g. a stack vs. pocket multi-slide, or a plain hinged door vs. one
 * with a sidelight) — dropping size exposed that those really are FORM differences that
 * belong in the name, not accidental byproducts of a size that no longer varies by type.
 */
function openingName(category: 'door' | 'window', params: Record<string, ParamValue>): string {
  const opLabel = category === 'door'
    ? DOOR_OP_LABEL.get(String(params.operation)) ?? 'Hinged'
    : WINDOW_OP_LABEL.get(String(params.operation)) ?? 'Fixed'
  const extras: string[] = []
  // HEAD SHAPE PER INSTANCE (2026-10-30): the head (arched / segmental / raked) is per
  // door/window now, so it no longer names the type (it named it 2026-09-22 → 10-30)
  // DOOR LEAF round (2026-10-07): construction, glass and sidelights/transom are per DOOR now
  // (the type is its operation), so they are no longer part of a door type's name
  if (category === 'door' && params.parkMode === 'pocket') extras.push('Pocket')
  // SITE GATES: a pair and the infill style are FORM differences — the default
  // picket-over-panel look needs no call-out, the others do
  if (category === 'door' && params.operation === 'gateSwing' && params.gateLeaves === 'double') extras.push('Pair')
  if (category === 'door' && (params.operation === 'gateSwing' || params.operation === 'gateSlide')) {
    const style = GATE_STYLE_LABEL[String(params.gateStyle)]
    if (style) extras.push(style)
  }
  const extra = extras.length ? ` (${extras.join(', ')})` : ''
  return `${opLabel}${extra}`
}

const CASEWORK_STYLE_LABEL: Record<string, string> = { base: 'Base', upper: 'Upper', tall: 'Tall' }

/** Kind (style) + depth family — WIDTH deliberately excluded (it's an instance param now). */
function caseworkName(params: Record<string, ParamValue>): string {
  // CLOSET ROD round: a closet is not a cabinet — its own noun, keyed by the hanging
  // depth and rod height (two closets differ by those, not by a box)
  if (String(params.style) === 'closet') {
    return `Closet Rod & Shelf — ${formatFtIn((params.depthFt as number) || 2)} Deep, Rod at ${formatFtIn((params.rodHeightFt as number) || 5.5)}`
  }
  const style = CASEWORK_STYLE_LABEL[String(params.style)] ?? 'Base'
  const depth = formatFtIn((params.depthFt as number) || 2)
  return `${style} Cabinet — ${depth} Deep`
}

/** FOOTINGS round: `Footing 1'-6" × 1'-0"`, material appended when set. */
function foundationName(
  params: Record<string, ParamValue>,
  materials: MaterialRecord[],
): string {
  const mat = materialName(materials, params.material as string)
  const suffix = mat ? ` · ${mat}` : ''
  const w = formatFtIn((params.widthFt as number) || 1.5)
  const t = formatFtIn((params.thicknessFt as number) || 10 / 12)
  return `Footing ${w} × ${t}${suffix}`
}

/**
 * STEM FINISH LAYERS round (2026-08-17): a stem TYPE without an assembly keeps its
 * pre-round flat name (`Stem Wall 0'-8"`, material appended when set) — every stem
 * type in a live model today has no assemblyId, and this must never rename them
 * out from under an untouched model (see the FIRM-LIBRARY TRAP note above). A stem
 * WITH an assembly gets the wall-style makeup name instead, since it now carries
 * real core+finish layers the same way a wall does — `assemblyThickness` on the
 * assembly replaces the flat `widthFt` param as the type's true section width.
 */
function stemName(
  params: Record<string, ParamValue>,
  assembly: AssemblyRecord | null,
  materials: MaterialRecord[],
): string {
  if (!assembly || !assembly.layers.length) {
    const mat = materialName(materials, params.material as string)
    const suffix = mat ? ` · ${mat}` : ''
    return `Stem Wall ${formatFtIn((params.widthFt as number) || 8 / 12)}${suffix}`
  }
  const makeup = assemblyMakeup(assembly, materials)
  const thickness = formatFtIn(assemblyThickness(assembly))
  return `Stem Wall ${thickness} — ${makeup}`
}

/**
 * ELECTRICAL FIXTURES round: WALL kinds get kind label ONLY (`Duplex Outlet` /
 * `Switch`) — mount height is a plain INSTANCE param (2026-08-12 correction),
 * never named on the type: the same reasoning that keeps a door's sillFt or an
 * object's heightOffsetFt out of THEIR names.
 *
 * CEILING HOSTING round (2026-08-23): CEILING kinds moved in from the `object`
 * category, where they were a curated size/shape MATRIX with hand-written names
 * (`Recessed Can 4" Round`, `Pendant Light 12"`…) — several distinct types per
 * kind, unlike every wall kind's single type. Kind-only naming would collapse
 * all of them onto one name, and ensureType's dedup-by-name would then silently
 * skip creating the rest at every doc open. Reproduces the exact hand-written
 * format instead, so the seed roster (seed.ts) and this recipe agree.
 */
function fixtureName(params: Record<string, ParamValue>): string {
  const kind = String(params.fixtureKind || 'outlet')
  if (isCeilingFixtureKind(kind)) {
    const inches = Math.round(((params.widthFt as number) || 0) * 12)
    if (kind === 'recessedCan') {
      const shape = params.trimShape === 'square' ? 'Square' : 'Round'
      return `Recessed Can ${inches}" ${shape}`
    }
    if (kind === 'pendant') return `Pendant Light ${inches}"`
    // LIGHT STYLES (2026-10-07): one generic Chandelier type — its look is the instance's style
    if (kind === 'chandelier') return fixtureKindLabel(kind)
    // CEILING DEVICES (bim-plan/28 round 3): detectors are one size; a register is named by
    // its face size, the way it is ordered (12" × 6")
    if (kind === 'smokeDetector' || kind === 'coDetector') return fixtureKindLabel(kind)
    if (kind === 'supplyRegister' || kind === 'returnRegister') {
      return `${fixtureKindLabel(kind)} ${inches}" × ${Math.round(((params.depthFt as number) || 0) * 12)}"`
    }
    return `Flush Mount ${inches}"`   // flushMount
  }
  // FIT-OUT CATALOG (2026-09-30): the SIZED bath kinds come in several sizes, so the
  // size is part of the name (the ceiling-kind reasoning above) — otherwise every
  // size collapses onto one name and ensureType seeds only the first
  const inch = (v: unknown) => Math.round(((v as number) || 0) * 12)
  if (kind === 'grabBar' || kind === 'towelBar') return `${fixtureKindLabel(kind)} ${inch(params.widthFt)}"`
  if (kind === 'mirror' || kind === 'medicineCabinet') {
    return `${fixtureKindLabel(kind)} ${inch(params.widthFt)}" × ${inch(params.heightFt)}"`
  }
  return fixtureKindLabel(kind)
}

/**
 * Derive a type's name from its own RESOLVED type params (schema defaults already
 * merged — callers pass `resolveSchemaParams(cat.typeParams, type.params).params`).
 * Returns null for a category not in NAME_RECIPE_CATEGORIES — callers keep whatever
 * manual name is already stored.
 */
export function deriveTypeName(
  category: CategoryId,
  params: Record<string, ParamValue>,
  assembly: AssemblyRecord | null,
  materials: MaterialRecord[],
): string | null {
  switch (category) {
    case 'wall':
    case 'floor':
    case 'roof':
    case 'ceiling':
      return assemblyBackedName(category, params, assembly, materials)
    case 'door':
    case 'window':
      return openingName(category, params)
    case 'casework':
      return caseworkName(params)
    case 'footing':
      return foundationName(params, materials)
    case 'stem':
      return stemName(params, assembly, materials)
    case 'fixture':
      return fixtureName(params)
    default:
      return null
  }
}
